import { Injectable, signal, computed } from '@angular/core'
import { Observable, BehaviorSubject, distinctUntilChanged, map } from 'rxjs'

/**
 * Generic base store interface that defines the contract for all stores.
 * Provides a consistent API across different store implementations.
 */
export interface BaseStore<T> {
  readonly state: () => T
  readonly loading: () => boolean
  readonly error: () => string | null

  setState(state: Partial<T>): void
  setLoading(loading: boolean): void
  setError(error: string | null): void
  reset(): void
}

/**
 * Generic signal-based store that can be extended for any application state.
 *
 * Features:
 * - Signal-based state management for reactive UI updates
 * - Built-in loading and error states
 * - Type-safe state updates
 * - Computed selectors
 * - Observable compatibility
 *
 * Usage:
 * ```typescript
 * interface UserState {
 *   users: User[];
 *   selectedUser: User | null;
 * }
 *
 * @Injectable({ providedIn: 'root' })
 * export class UserStore extends BaseSignalStore<UserState> {
 *   constructor() {
 *     super({
 *       users: [],
 *       selectedUser: null
 *     });
 *   }
 * }
 * ```
 */
@Injectable()
export abstract class BaseSignalStore<T> implements BaseStore<T> {
  private readonly _state = signal<T>({} as T)
  private readonly _loading = signal<boolean>(false)
  private readonly _error = signal<string | null>(null)

  // Observable subjects for RxJS integration
  private readonly _stateSubject = new BehaviorSubject<T>({} as T)
  private readonly _loadingSubject = new BehaviorSubject<boolean>(false)
  private readonly _errorSubject = new BehaviorSubject<string | null>(null)

  constructor(initialState: T) {
    this._state.set(initialState)
    this._stateSubject.next(initialState)
  }

  // Signal-based getters (implementing BaseStore interface)
  readonly state = () => this._state()
  readonly loading = () => this._loading()
  readonly error = () => this._error()

  // Signal-based reactive getters
  readonly stateSignal = this._state.asReadonly()
  readonly loadingSignal = this._loading.asReadonly()
  readonly errorSignal = this._error.asReadonly()

  // Observable-based getters for RxJS integration
  readonly state$ = this._stateSubject.asObservable().pipe(distinctUntilChanged())
  readonly loading$ = this._loadingSubject.asObservable().pipe(distinctUntilChanged())
  readonly error$ = this._errorSubject.asObservable().pipe(distinctUntilChanged())

  /**
   * Updates the state with partial data.
   * @param newState Partial state update
   */
  setState(newState: Partial<T>): void {
    const currentState = this._state()
    const updatedState = { ...currentState, ...newState }
    this._state.set(updatedState)
    this._stateSubject.next(updatedState)
  }

  /**
   * Sets the loading state.
   * @param loading Loading state
   */
  setLoading(loading: boolean): void {
    this._loading.set(loading)
    this._loadingSubject.next(loading)
  }

  /**
   * Sets the error state.
   * @param error Error message or null
   */
  setError(error: string | null): void {
    this._error.set(error)
    this._errorSubject.next(error)
  }

  /**
   * Resets the store to initial state.
   */
  reset(): void {
    const initialState = this.getInitialState()
    this._state.set(initialState)
    this._stateSubject.next(initialState)
    this._loading.set(false)
    this._loadingSubject.next(false)
    this._error.set(null)
    this._errorSubject.next(null)
  }

  /**
   * Creates a computed signal from a selector function.
   * @param selector Function to select part of the state
   * @returns Computed signal
   */
  protected select<R>(selector: (state: T) => R) {
    return computed(() => selector(this._state()))
  }

  /**
   * Creates an observable from a selector function.
   * @param selector Function to select part of the state
   * @returns Observable
   */
  protected select$<R>(selector: (state: T) => R): Observable<R> {
    return this.state$.pipe(map(selector), distinctUntilChanged())
  }

  /**
   * Gets the initial state. Override in subclasses if needed.
   * @returns Initial state
   */
  protected getInitialState(): T {
    return this._state()
  }

  /**
   * Updates state with a function for complex updates.
   * @param updater Function that receives current state and returns new state
   */
  protected updateState(updater: (currentState: T) => T): void {
    const currentState = this._state()
    const newState = updater(currentState)
    this._state.set(newState)
    this._stateSubject.next(newState)
  }
}
